import React, { useEffect, useRef, useState } from 'react';
import { addParameterToUrl } from '@portal-internet/core';
import { useData } from 'hooks/useData';
import { useViewport } from 'hooks/corporatiu/useViewport';
import { useEmitter } from 'hooks/useEmitter';
import ItemResultatCerca from './ItemResultatCerca';
import Icona from 'components/corporatiu/Icona';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import styles from './resultatscerca.module.scss';

const NoResult = (props) => {
    const { items } = props;

    return (
        <>
            {__isEmpty(items) && (
                <div className={styles.noTrobat} aria-live='polite' aria-atomic='true'>
                    <p className={styles.destacatNoTrobat}>Ho sentim, la teva cerca no ha obtingut cap resultat.</p>
                    <p>
                        Sisplau, comprova si has escrit bé les paraules que cercaves o intenta usar-ne altres de similiars o més
                        generals en una nova cerca.
                    </p>
                </div>
            )}
        </>
    );
};

const Paginador = (props) => {
    const { items, paginacio, loadPage } = props;
    const { total_pagines, pagina_actual } = paginacio;
    const { isMobile, isOnlyMobile } = useViewport();

    if (__isEmpty(items)) return <></>;

    const getPageNumbers = (numPagesShow = 7) => {
        const pages = [];

        const half = Math.floor(numPagesShow / 2);
        let start = Math.max(1, pagina_actual - half);
        const end = Math.min(total_pagines, start + numPagesShow - 1);

        if (end - start + 1 < numPagesShow) {
            start = Math.max(1, end - numPagesShow + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };

    const pages = getPageNumbers(isOnlyMobile ? 3 : isMobile ? 5 : 7);

    return (
        <div className={styles.paginador}>
            <div className={styles.resumePag}>
                Pàgina {pagina_actual} de {total_pagines}
            </div>
            <ul className={styles.pages}>
                {pagina_actual > 1 && (
                    <>
                        <li className={styles.pagina} onClick={() => loadPage(1)}>
                            <Icona className={styles.icona} icona='fletxa_doble_esquerra' isBlanc={false} inline={true} />
                        </li>
                        <li className={styles.pagina} onClick={() => loadPage(pagina_actual - 1)}>
                            {isMobile ? (
                                <Icona className={styles.icona} icona='fletxa_esquerra' isBlanc={false} inline={true} />
                            ) : (
                                'ANTERIOR'
                            )}
                        </li>
                    </>
                )}
                {pages.map((numPag, i) => {
                    const stylePag = clsx(styles.pagina, pagina_actual === numPag && styles.pagActiva);
                    return (
                        <li key={`paginador-pag-${i}`} className={stylePag} onClick={() => loadPage(numPag)}>
                            {numPag}
                        </li>
                    );
                })}
                {pagina_actual < total_pagines && (
                    <>
                        <li className={styles.pagina} onClick={() => loadPage(pagina_actual + 1)}>
                            {isMobile ? (
                                <Icona className={styles.icona} icona='fletxa_dreta' isBlanc={false} inline={true} />
                            ) : (
                                'SEGÜENT'
                            )}
                        </li>
                        <li className={styles.pagina} onClick={() => loadPage(total_pagines)}>
                            <Icona className={styles.icona} icona='fletxa_doble_dreta' inline={true} />
                        </li>
                    </>
                )}
            </ul>
        </div>
    );
};

const LlistatCerca = (props) => {
    const { items, searchValue, searchDate, paginacio, portal, mostrarResumResultats, searchProfile } = props;
    if (__isEmpty(items)) return <></>;
    const numItems = paginacio?.total_items;

    return (
        <div className={styles.resultats}>
            {mostrarResumResultats && (
                <div className={styles.resumResultats} aria-live='polite' aria-atomic='true'>
                    <p className={styles.numResultats}>
                        S&apos;han trobat <span className={styles.textDestacat}>{numItems}</span> resultats
                        {!__isEmpty(searchValue) && (
                            <>
                                {' '}
                                per <span className={styles.textDestacat}>{searchValue}</span>
                            </>
                        )}
                    </p>
                </div>
            )}
            <ul className={styles.llistaResultats}>
                {items.map((item, index) => (
                    <li
                        key={`item-resultat-${encodeURIComponent(searchValue)}-${item?.id}`}
                        data-testid={`item-resultat-cerca-${index}`}
                    >
                        <ItemResultatCerca
                            item={item}
                            portal={portal}
                            searchParams={{ searchValue, searchDate, searchProfile }}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
};

const ResultatsCerca = (props) => {
    const {
        idEscolta,
        url: urlCercaByLayout,
        portal,
        items: itemsByLayout,
        paginacio: paginacioByLayout,
        cercaPerQueryParam,
        routerQuery,
        mostrarResumResultats = true
    } = props;

    const { data: itemsByCerca, paginacio: paginacioByCerca, getDades } = useData();
    const { onEmitter } = useEmitter();

    const [searchParams, setSearchParams] = useState({});
    const items = __isEmpty(paginacioByCerca) ? itemsByLayout : itemsByCerca;
    const paginacio = __isEmpty(paginacioByCerca) ? paginacioByLayout : paginacioByCerca;
    const searchValue = searchParams?.searchValue || cercaPerQueryParam;
    const searchFromDate = searchParams?.fromDate || '';
    const searchToDate = searchParams?.toDate || '';
    const searchDate = searchFromDate + (searchToDate ? '-' + searchToDate : '') || routerQuery.data_publicacio || '';
    const searchProfile = searchParams?.searchProfile || routerQuery.profile;

    const cercaRef = useRef(null);

    const loadPage = (numPage) => {
        const url = searchParams?.url || urlCercaByLayout;
        const urlPage = addParameterToUrl(url, 'pagina', numPage);
        getDades(urlPage);
        cercaRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    onEmitter(idEscolta, (value) => {
        setSearchParams(value);
    });

    useEffect(() => {
        if (searchParams?.url) getDades(searchParams?.url);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    return (
        <div ref={cercaRef}>
            <NoResult items={items} />
            <LlistatCerca
                items={items}
                mostrarResumResultats={mostrarResumResultats}
                searchValue={searchValue}
                searchDate={searchDate}
                searchProfile={searchProfile}
                paginacio={paginacio}
                portal={portal}
            />
            <Paginador items={items} paginacio={paginacio} loadPage={loadPage} />
        </div>
    );
};

export default ResultatsCerca;
